<template>
  <div class="approval-list">
    <!-- 审批列表（企业微信手机端） -->
    <mobile-list-header>
      <!-- 第一行：搜索框占满整行，左输入框 + 右搜索按钮 -->
      <div class="search-bar">
        <div class="search-input">
          <yq-icon class="icon" name="element-ui_search"/>
          <input v-model="searchInfo.searchKey" placeholder="事项/单号/发起人" @keyup.enter="resetSearch"/>
          <yq-icon v-if="searchInfo.searchKey" class="icon clear" name="element-ui_close" @click="clearKey"/>
        </div>
        <div class="search-btn" @click="resetSearch">搜索</div>
      </div>
      <!-- 第二行：查询条件，无背景色，超过一屏横向滑动 -->
      <div class="filter-row">
        <mobile-condition-select v-model="searchInfo.businessType" :data="templateList"
                                 label-column="businessName" code-column="businessType" type="list"
                                 placeholder="业务类型" @change="resetSearch"/>
        <!-- 处理状态只有「未处理 / 已处理」两种，后端默认未处理（没有「全部」），所以做成点一下切换的 chip -->
        <div class="status-chip" @click="toggleHandled">{{ handledLabel }}</div>
      </div>
    </mobile-list-header>
    <mobile-list :loading="loading" :has-more="searchInfo.hasMore" :query-list="queryList" :refresh="resetSearch" ref="mobileList">
      <!-- 统计：四个数按当前登录人算，是「与我相关」的口径 -->
      <div class="stat-grid">
        <div class="stat-card">
          <div class="label">待我处理</div>
          <div class="value warning">{{ stat.todoCount || 0 }}<span class="unit">条</span></div>
          <div class="sub">指派给我、尚未处理</div>
        </div>
        <div class="stat-card">
          <div class="label">我发起的</div>
          <div class="value primary">{{ stat.startedCount || 0 }}<span class="unit">条</span></div>
          <div class="sub">仍在审批中</div>
        </div>
        <div class="stat-card">
          <div class="label">已通过</div>
          <div class="value success">{{ stat.passedCount || 0 }}<span class="unit">条</span></div>
          <div class="sub">我发起且已完结</div>
        </div>
        <div class="stat-card">
          <div class="label">已驳回</div>
          <div class="value danger">{{ stat.rejectedCount || 0 }}<span class="unit">条</span></div>
          <div class="sub">含被撤回的单据</div>
        </div>
      </div>

      <list-card class="approval-item" v-for="item in dataList" :key="item.id" :label="item.bizTitle" @click="toDetail(item)">
        <template #desc>
          <span class="task-tag" :class="item.taskTypeName === '抄送' ? 'tag-info' : 'tag-warning'">{{ item.taskTypeName || "审批" }}</span>
          <yq-icon class="item-arrow" name="element-ui_arrow-right"/>
        </template>
        <data-column v-if="item.processName" label="流程">{{ item.processName }}</data-column>
        <data-column label="业务类型">{{ item.businessName || "—" }}</data-column>
        <data-column label="当前节点">{{ nodeText(item) }}</data-column>
        <data-column label="发起人">{{ item.startUserName || "—" }}</data-column>
        <data-column label="发起时间">{{ fmtTime(item.startTime) }}</data-column>
      </list-card>
    </mobile-list>
  </div>
</template>

<script>

import {useRoute, useRouter} from "vue-router";
import MobileList from "@/components/qywx/layout/MobileList.vue";
import MobileListHeader from "@/components/qywx/layout/MobileListHeader.vue";
import MobileConditionSelect from "@/components/qywx/data/MobileConditionSelect.vue";
import ListCard from "@/components/qywx/layout/ListCard.vue";
import DataColumn from "@/components/qywx/data/DataColumn.vue";
import YqIcon from "@/components/YqIcon.vue";
import checkApi from "@/api/dz-project/project/checkApi";
import commonApi from "@/api/commonApi";

/** 签署方式，与后端 ProcessFlowConstant.CheckType 对齐 */
const CHECK_TYPE = {1: "或签", 2: "会签", 3: "自动通过", 4: "并签"};

export default {
  setup() {
    return {
      router: useRouter(),
      route: useRoute(),
    }
  },
  name: "AL",
  components: {
    YqIcon, DataColumn, ListCard, MobileConditionSelect, MobileListHeader, MobileList
  },
  data() {
    return {
      loading: false,
      searchInfo: {
        searchKey: "",
        businessType: "",
        // 默认只看未处理：进待办中心是来干活的，不是来翻历史的（与 web 端一致）
        handled: false,
        pageNumber: 0,
        pageSize: 20,
        hasMore: true, // 初始为 true，列表挂载后由无限滚动自动触发首次查询
      },
      dataList: [],
      stat: {},
      templateList: [], // 业务类型（已对接工作流的业务清单）
    };
  },
  computed: {
    handledLabel() {
      return this.searchInfo.handled ? "已处理" : "未处理";
    }
  },
  created() {
    // 业务类型走通用接口：与工作流列表、送审中心共用同一份字典
    commonApi.queryBusinessTemplateList().then(list => this.templateList = list || []);
    this.loadStat();
  },
  methods: {
    queryList() {
      this.loading = true;
      this.searchInfo.pageNumber++;
      // handled 是布尔值，false 也要带上（后端默认 false，但显式传更清楚）
      checkApi.queryTodo({
        searchKey: this.searchInfo.searchKey,
        businessType: this.searchInfo.businessType,
        handled: this.searchInfo.handled,
        pageNumber: this.searchInfo.pageNumber,
        pageSize: this.searchInfo.pageSize,
      }).then(res => {
        this.dataList = this.dataList.concat((res && res.records) || []);
        this.searchInfo.hasMore = res.pageCount > this.searchInfo.pageNumber;
        this.loading = false;
        this.$refs.mobileList.hideRefreshTitle();
      }).catch(() => {
        this.loading = false;
      })
    },
    // 顶部四张统计卡：以「我」为维度
    loadStat() {
      checkApi.queryStat({businessType: this.searchInfo.businessType}).then(res => {
        this.stat = res || {};
      });
    },
    resetSearch() {
      this.searchInfo.pageNumber = 0;
      this.dataList = [];
      this.searchInfo.hasMore = true;
      this.loadStat();
      this.queryList();
    },
    clearKey() {
      this.searchInfo.searchKey = "";
      this.resetSearch();
    },
    /** 未处理 ⇄ 已处理 */
    toggleHandled() {
      this.searchInfo.handled = !this.searchInfo.handled;
      this.resetSearch();
    },
    nodeText(item) {
      const node = item.nodeName || "—";
      const checkType = CHECK_TYPE[item.checkType];
      return checkType ? node + "（" + checkType + "）" : node;
    },
    fmtTime(value) {
      if (!value) {
        return "—";
      }
      return String(value).replace("T", " ").slice(0, 16);
    },
    /**
     * 进审批详情页。
     *
     * 带 taskId 一起过去：同一实例上我可能既有历史任务、又有当前待办，
     * 只给 instanceId 的话详情页要靠猜，容易把「查看旧任务」点成「处理新任务」。
     * 操作模式不传 —— 由详情页按任务自身推导。
     */
    toDetail(item) {
      this.router.push({
        path: "/qywx/approval/detail",
        query: {instanceId: item.instanceId, taskId: item.id},
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.approval-list{
  height: 100%;
  display: flex;
  flex-direction: column;
  // 悬浮按钮的定位祖先
  position: relative;
}
// 第一行：搜索框占满整行，左输入框 + 右搜索按钮
.search-bar{
  display: flex;
  align-items: center;
  height: 44px;
  padding-left: 12px;
  border-radius: 999px;
  background: var(--yq-background-color);
  overflow: hidden;
  .search-input{
    flex: 1;
    display: flex;
    align-items: center;
    min-width: 0;
    height: 100%;
    padding-right: 12px;
    .icon{
      flex: none;
      color: var(--yq-font-color-desc);
      font-size: var(--yq-font-size-mini-mobile);
      &.clear{
        margin-left: 6px;
      }
    }
    input{
      flex: 1;
      min-width: 0;
      height: 100%;
      margin-left: 6px;
      border: none;
      outline: none;
      background: transparent;
      font-size: var(--yq-font-size-mini-mobile);
      color: var(--yq-font-color-base);
      &::placeholder{
        color: var(--yq-font-color-desc);
      }
    }
  }
  .search-btn{
    flex: none;
    display: flex;
    align-items: center;
    height: 100%;
    padding: 0 18px;
    background: var(--el-color-primary);
    color: #FFF;
    font-size: var(--yq-font-size-mini-mobile);
  }
}
// 第二行：查询条件，无背景色，超过一屏横向滑动
.filter-row{
  display: flex;
  align-items: center;
  gap: 18px;
  margin-top: 10px;
  overflow-x: auto;
  scrollbar-width: none;
  &::-webkit-scrollbar{
    display: none;
  }
  > *{
    flex: none;
  }
}
// 处理状态：一个 chip 显示当前值，点一下切换
// ★ 用主题色：它和业务类型不一样，是「换个看数据的方向」（看没处理的 / 看处理过的）
.status-chip{
  font-size: 12px;
  color: var(--el-color-primary);
}
// 统计卡（四张）
.stat-grid{
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  padding-top: 10px;
  .stat-card{
    background: #FFF;
    border-radius: 6px;
    padding: 10px 12px;
    box-shadow: 0 0 10px var(--yq-shadow);
    .label{
      font-size: var(--yq-font-size-mini-mobile);
      color: var(--yq-font-color-desc);
    }
    .value{
      margin-top: 4px;
      font-size: var(--yq-font-size-large-mobile);
      font-weight: 700;
      color: var(--yq-font-color-base);
      &.warning{
        color: #e6a23c;
      }
      &.primary{
        color: #409eff;
      }
      &.success{
        color: #67c23a;
      }
      &.danger{
        color: #f56c6c;
      }
      .unit{
        margin-left: 2px;
        font-size: var(--yq-font-size-mini-mobile);
        font-weight: 400;
      }
    }
    .sub{
      margin-top: 2px;
      font-size: var(--yq-font-size-mini-mobile);
      color: var(--yq-font-color-desc);
    }
  }
}
.approval-item{
  :deep(.card-title){
    // 审批事项标题通常较长，右上角还有一个任务类型标签，允许折两行
    height: auto;
    min-height: 60px;
    .name{
      white-space: normal;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      overflow: hidden;
      line-height: 1.3;
    }
    .desc{
      display: flex;
      align-items: center;
      gap: 6px;
    }
  }
  .task-tag{
    flex: none;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: var(--yq-font-size-mini-mobile);
    &.tag-info{
      color: var(--el-color-info);
      background: var(--el-fill-color-light);
    }
    &.tag-warning{
      color: var(--el-color-warning);
      background: var(--el-color-warning-light-9);
    }
  }
  .item-arrow{
    color: var(--yq-font-color-desc);
    font-size: var(--yq-font-size-mini-mobile);
  }
}
</style>
